import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { triggerRefresh } from "@/hooks/useRefresh";
import { peso, num } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, X, PackageCheck } from "lucide-react";

export const STATUS_BADGE = {
  draft: "bg-slate-100 text-slate-700 hover:bg-slate-100",
  sent: "bg-blue-100 text-blue-800 hover:bg-blue-100",
  partial: "bg-amber-100 text-amber-800 hover:bg-amber-100",
  received: "bg-green-100 text-green-800 hover:bg-green-100",
};

const newLine = () => ({ _key: Math.random().toString(36).slice(2), item_id: "", qty_ordered: "", unit_cost: "", vat_inclusive: true });
const emptyForm = () => ({ branch_id: "", supplier_id: "", lines: [newLine()] });

export function POCreateDialog({ open, onOpenChange, items, suppliers }) {
  const { writableBranches } = useBranch();
  const [form, setForm] = useState(emptyForm());

  const setLine = (i, patch) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], ...patch };
    setForm({ ...form, lines });
  };

  const pickItem = (i, item_id) => {
    const it = items.find((x) => x.item_id === item_id);
    setLine(i, { item_id, unit_cost: String(it?.unit_cost ?? ""), vat_inclusive: it?.vat_inclusive ?? true });
  };

  const createPO = async (e) => {
    e.preventDefault();
    try {
      await api.post("/purchase-orders", {
        branch_id: form.branch_id, supplier_id: form.supplier_id,
        lines: form.lines.filter((l) => l.item_id && l.qty_ordered).map((l) => ({
          item_id: l.item_id, qty_ordered: parseFloat(l.qty_ordered),
          unit_cost: parseFloat(l.unit_cost || 0), vat_inclusive: l.vat_inclusive,
        })),
      });
      toast.success("PO created as draft");
      onOpenChange(false);
      setForm(emptyForm());
      triggerRefresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="po-create-dialog">
        <DialogHeader><DialogTitle>New purchase order</DialogTitle></DialogHeader>
        <form onSubmit={createPO} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Branch</Label>
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger data-testid="po-branch-select"><SelectValue placeholder="Branch" /></SelectTrigger>
                <SelectContent>{writableBranches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-2"><Label>Supplier</Label>
              <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                <SelectTrigger data-testid="po-supplier-select"><SelectValue placeholder="Supplier" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="space-y-2">
            <Label>Lines (unit cost ₱, VAT flag from item)</Label>
            {form.lines.map((l, i) => (
              <div key={l._key} className="flex gap-2 items-center">
                <Select value={l.item_id} onValueChange={(v) => pickItem(i, v)}>
                  <SelectTrigger className="flex-1" data-testid={`po-line-item-${i}`}><SelectValue placeholder="Item" /></SelectTrigger>
                  <SelectContent className="max-h-64">{items.map((it) => <SelectItem key={it.item_id} value={it.item_id}>{it.name} ({it.unit})</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" step="any" min="0" placeholder="Qty" className="w-20" value={l.qty_ordered}
                  onChange={(e) => setLine(i, { qty_ordered: e.target.value })} data-testid={`po-line-qty-${i}`} />
                <Input type="number" step="any" min="0" placeholder="₱ cost" className="w-24" value={l.unit_cost}
                  onChange={(e) => setLine(i, { unit_cost: e.target.value })} data-testid={`po-line-cost-${i}`} />
                {form.lines.length > 1 && (
                  <Button type="button" size="icon" variant="ghost" onClick={() => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) })}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" data-testid="po-add-line-btn"
              onClick={() => setForm({ ...form, lines: [...form.lines, newLine()] })}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add line
            </Button>
          </div>
          <Button type="submit" className="w-full" disabled={!form.branch_id || !form.supplier_id} data-testid="po-create-btn">
            Create draft PO
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function POViewDialog({ po, onClose, canEdit }) {
  const [recvQty, setRecvQty] = useState({});

  useEffect(() => {
    if (!po) return;
    const init = {};
    po.lines.forEach((l) => { init[l.item_id] = String(Math.max(l.qty_ordered - l.qty_received, 0) || ""); });
    setRecvQty(init);
  }, [po]);

  const receivable = po && ["sent", "partial"].includes(po.status) && canEdit;

  const receivePO = async () => {
    try {
      const lines = Object.entries(recvQty).filter(([, q]) => parseFloat(q) > 0)
        .map(([item_id, q]) => ({ item_id, qty: parseFloat(q) }));
      const { data } = await api.post(`/purchase-orders/${po.po_id}/receive`, { lines });
      toast.success(`Stock posted — PO now ${data.status}`);
      onClose();
      triggerRefresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Dialog open={!!po} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="po-view-dialog">
        {po && (
          <>
            <DialogHeader>
              <DialogTitle>{po.po_number} · {po.supplier_name} <Badge className={`ml-2 ${STATUS_BADGE[po.status]}`}>{po.status}</Badge></DialogTitle>
            </DialogHeader>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-muted-foreground text-left">
                  <tr className="border-b">
                    <th className="py-2 pr-2">Item</th><th className="py-2 px-2 text-right">Ordered</th>
                    <th className="py-2 px-2 text-right">Received</th>
                    <th className="py-2 px-2 text-right">Net</th><th className="py-2 px-2 text-right">VAT</th>
                    <th className="py-2 px-2 text-right">Gross</th>
                    {receivable && <th className="py-2 pl-2 text-right">Receive now</th>}
                  </tr>
                </thead>
                <tbody>
                  {po.lines.map((l) => (
                    <tr key={l.item_id} className="border-b last:border-0">
                      <td className="py-2 pr-2 font-medium">{l.item_name}</td>
                      <td className="py-2 px-2 text-right">{num(l.qty_ordered)} {l.unit}</td>
                      <td className="py-2 px-2 text-right">{num(l.qty_received)}</td>
                      <td className="py-2 px-2 text-right">{peso(l.net)}</td>
                      <td className="py-2 px-2 text-right">{peso(l.vat)}</td>
                      <td className="py-2 px-2 text-right">{peso(l.gross)}</td>
                      {receivable && (
                        <td className="py-2 pl-2 text-right">
                          <Input type="number" step="any" min="0" className="w-20 ml-auto h-8" value={recvQty[l.item_id] || ""}
                            onChange={(e) => setRecvQty({ ...recvQty, [l.item_id]: e.target.value })}
                            data-testid={`po-receive-qty-${l.item_id}`} />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap justify-between items-center gap-3 pt-2 border-t">
              <div className="text-sm">
                <span className="text-muted-foreground">Net {peso(po.net_total)} · VAT (12%) {peso(po.vat_total)} · </span>
                <span className="font-semibold">Gross {peso(po.gross_total)}</span>
              </div>
              {receivable && (
                <Button onClick={receivePO} data-testid="po-receive-btn">
                  <PackageCheck className="h-4 w-4 mr-1" /> Post receipt
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
